import { ref, onMounted, computed, watch, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import vSelect from "vue-select";
import Swal from "sweetalert2";
import "vue-select/dist/vue-select.css";
import Sidebar from "@/components/Sidebar.vue";
import Header from "@/components/HeaderDashboard.vue";
import { ProductoService } from "@/services/ProductoService";
import { config } from "@/config";
import { RepuestoService } from "@/services/RepuestoService";
import { ModeloService } from "@/services/ModeloService";
import { MarcaService } from "@/services/MarcaService";
import { formatNumber } from "@/utils/formatNumber";
export default {
    name: "Productos",
    components: {
        Sidebar,
        Header,
        vSelect,
    },
    setup() {
        const { ListIcon, TypeIcon, HashIcon, ToggleLeftIcon, CalendarIcon, MaximizeIcon } = require('lucide-vue-next');
        const productoForm = ref({
            id: 0,
            nombre: "", // Mantenemos internamente si se necesita, pero no se envía
            codigoBarrasSistema: "",
            codigoBarrasReferencia: "",
            modeloID: 0,
            repuestoID: 0,
            codigoUnico: "",
            marcaId: 0,
            color: "Ejemplo",
            detalles: "",
            precioVenta: 0,
            pesoKG: 0,
            activo: true,
            imagenesDetalle: [],
            atributosDesplegables: [],
        });
        const coloresDisponibles = [
            "NEGRO",
            "VERDE",
            "ROJO",
            "AZUL",
            "AMARILLO",
            "NARANJA",
            "MORADO",
            "GRIS",
            "BLANCO",
            "MARRÓN",
        ];
        const precioVentaFormatted = ref("");
        watch(() => productoForm.value.precioVenta, (newVal) => {
            precioVentaFormatted.value = formatCurrency(newVal);
        }, { immediate: true });
        function formatCurrency(value) {
            if (!value)
                return "";
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
            }).format(+value).replace("$", ""); // quitamos el símbolo si ya lo tienes por separado
        }
        // Variable para aplicar color
        const applyColor = ref(false);
        const coloresSeleccionados = ref([]);
        const isCameraOpen = ref(false);
        const cameraFacing = ref('environment');
        const video = ref(null);
        let cameraStream = null;
        const videoDevices = ref([]);
        const selectedDeviceId = ref("");
        const removeGreenScreen = ref(false); // Opción para eliminar fondo
        const apiKey = "QJs4Z49k2ZRoauoBPrzjVaQb"; // 🔥 Reemplaza con tu API Key
        const atributosDesplegables = ref([]);
        //REPUESTO
        const repuestos = ref([]);
        const searchRepuestos = ref("");
        const selectedRepuesto = ref(null);
        //MODELO
        const modelos = ref([]);
        const searchModelos = ref("");
        const selectedModelo = ref(null);
        //MARCA
        const marcas = ref([]);
        const searchMarcas = ref("");
        const selectedMarca = ref(null);
        const isLoading = ref(false);
        const router = useRouter();
        const route = useRoute();
        const isEditing = ref(false);
        const succesToast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
                popup: "success-toast", // Clase personalizada para el toast de error
            },
            didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
            },
        });
        const errorToast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
                popup: "error-toast", // Clase personalizada para el toast de error
            },
            didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
            },
        });
        watch(selectedRepuesto, (newRepuesto) => {
            if (newRepuesto !== null) {
                productoForm.value.repuestoID = newRepuesto;
            }
        });
        watch(selectedModelo, (newModelo) => {
            if (newModelo !== null) {
                productoForm.value.modeloID = newModelo;
            }
        });
        watch(selectedMarca, (newMarca) => {
            if (newMarca !== null) {
                productoForm.value.marcaId = newMarca;
            }
        });
        function onPrecioVentaInput(e) {
            const input = e.target.value;
            const clean = input.replace(/\D/g, ""); // quitar todo lo que no sea dígito
            // convertir a número entero (centavos)
            const centavos = parseInt(clean) || 0;
            // dividir entre 100 para tener valor decimal
            const decimalValue = centavos / 100;
            // guardar el valor real en el formulario como decimal
            productoForm.value.precioVenta = decimalValue;
            // mostrar el valor formateado (RD$ 1,234.56 por ejemplo)
            precioVentaFormatted.value = formatCurrency(decimalValue.toFixed(2));
        }
        function onAttributeInput(atributo, field) {
            const formattedField = field + "Formatted";
            const input = atributo[formattedField];
            const decimals = atributo.nombreAtributo?.unidad?.decimales ?? 0;
            // Quitar todo lo que no sea dígito
            const clean = input.replace(/\D/g, "");
            const num = parseInt(clean) || 0;
            // Dividir por la potencia de 10 según los decimales
            const value = num / Math.pow(10, decimals);
            // Guardar valor real
            atributo[field] = value;
            // Mostrar formateado con punto para miles y coma para decimales (usando util)
            atributo[formattedField] = formatNumber(value, decimals);
        }
        function getPlaceholder(atributo) {
            const decimals = atributo.nombreAtributo?.unidad?.decimales ?? 0;
            if (decimals <= 0)
                return "0";
            return "0." + "0".repeat(decimals);
        }
        function base64ToFile(base64String, filename) {
            // Verifica si la cadena Base64 es válida
            if (!isValidBase64(base64String)) {
                throw new Error("Cadena Base64 no válida.");
            }
            // Extrae la parte de los datos de la cadena Base64
            const arr = base64String.split(",");
            const mimeMatch = arr[0].match(/:(.*?);/);
            // Verifica si la coincidencia de MIME es válida
            if (!mimeMatch) {
                throw new Error("No se pudo extraer el tipo MIME.");
            }
            const mime = mimeMatch[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], filename, { type: mime });
        }
        function isValidBase64(base64String) {
            return (base64String.startsWith("data:image/") &&
                base64String.includes("base64,"));
        }
        async function saveProducto() {
            isLoading.value = true;
            try {
                if (!productoForm.value) {
                    console.error("productoForm.value es null o undefined");
                    return;
                }
                // 1. Validar Precio de Venta
                if (productoForm.value.precioVenta <= 0) {
                    errorToast.fire({
                        text: "El precio de venta debe ser mayor que cero.",
                        icon: "warning",
                    });
                    return;
                }
                // 2. Validar Repuesto Seleccionado
                if (!productoForm.value.repuestoID || productoForm.value.repuestoID === 0) {
                    errorToast.fire({
                        text: "Por favor, seleccione un repuesto.",
                        icon: "warning",
                    });
                    return;
                }
                const repuestoSeleccionado = repuestos.value.find(r => r.repuestoID === productoForm.value.repuestoID);
                // El nombre ya no se envía al backend, se genera dinámicamente allá.
                // Ensure at least one image is selected as portada if there are images
                if (productoForm.value.imagenesDetalle.length > 0) {
                    const hasPortada = productoForm.value.imagenesDetalle.some(img => img.portada);
                    if (!hasPortada) {
                        errorToast.fire({
                            text: "Por favor, seleccione una imagen como portada.",
                            icon: "warning",
                        });
                        return; // Prevent saving if no portada is explicitly set by user and images exist
                    }
                }
                else {
                    errorToast.fire({
                        text: "Por favor, agregue al menos una imagen para el producto.",
                        icon: "warning",
                    });
                    return; // Prevent saving if no images exist
                }
                const productoData = productoForm.value;
                // Crear el FormData
                const formData = new FormData();
                // Agregar cada propiedad del productoDto por separado
                formData.append("productoDto.CodigoBarrasSistema", productoData.codigoBarrasSistema || "");
                formData.append("productoDto.CodigoBarrasReferencia", productoData.codigoBarrasReferencia || "");
                if (productoData.repuestoID !== undefined) {
                    formData.append("productoDto.RepuestoID", productoData.repuestoID.toString());
                }
                formData.append("productoDto.ModeloID", productoData.modeloID ? productoData.modeloID.toString() : "0");
                formData.append("productoDto.CodigoUnico", productoData.codigoUnico || "");
                formData.append("productoDto.MarcaId", (productoData.marcaId || 0).toString());
                formData.append("productoDto.Color", productoData.color || "");
                formData.append("productoDto.Detalles", productoData.detalles || "");
                formData.append("productoDto.PesoKG", (productoData.pesoKG || 0).toString());
                formData.append("productoDto.PrecioVenta", (productoData.precioVenta || 0).toString());
                formData.append("productoDto.Activo", String(productoData.activo ?? true));
                // Adjuntar cada imagen (nuevas y existentes) con índices continuos
                productoData.imagenesDetalle.forEach((image, index) => {
                    // El ID ayuda al backend a saber si es nueva (0) o existente (>0)
                    formData.append(`imagenes[${index}].Id`, (image.id && !image.imagen) ? image.id.toString() : "0");
                    formData.append(`imagenes[${index}].Portada`, String(image.portada));
                    if (image.imagen instanceof File) {
                        formData.append(`imagenes[${index}].File`, image.imagen);
                    }
                });
                productoForm.value.atributosDesplegables.forEach((atributo, index) => {
                    // Obtener el ID del atributo, buscando en diferentes lugares por si acaso
                    const attrId = atributo.nombreAtributoID || (atributo.nombreAtributo && atributo.nombreAtributo.nombreAtributoID);
                    if (!attrId || attrId === 0) {
                        console.warn(`Saltando atributo en índice ${index} porque no tiene un ID válido:`, atributo);
                        return;
                    }
                    formData.append(`desplegables[${index}].id`, "0"); // Para la tabla pivote es nuevo
                    formData.append(`desplegables[${index}].nombreAtributoID`, attrId.toString());
                    const tipo = atributo.nombreAtributo?.tipoAtributo || 'desplegable';
                    if (tipo === 'desplegable') {
                        formData.append(`desplegables[${index}].valorID`, atributo.selectedValue ? atributo.selectedValue.toString() : "");
                        formData.append(`desplegables[${index}].ref`, atributo.ref || "NA"); // Mantenemos retrocompatibilidad si usaba ref
                    }
                    else if (tipo === 'rango') {
                        formData.append(`desplegables[${index}].valorMin`, atributo.valorMin?.toString() || "");
                        formData.append(`desplegables[${index}].valorMax`, atributo.valorMax?.toString() || "");
                        formData.append(`desplegables[${index}].ref`, "NA");
                    }
                    else {
                        // texto_libre, numerico, booleano, fecha
                        // Manejamos 'Ref' duro retrocompatible enviándolo en ref también si es necesario
                        if (atributo.nombreAtributo?.nombreAtributo === 'Ref') {
                            formData.append(`desplegables[${index}].ref`, atributo.valorLibre || "NA");
                        }
                        else {
                            formData.append(`desplegables[${index}].ref`, "NA");
                        }
                        formData.append(`desplegables[${index}].valorLibre`, atributo.valorLibre || "");
                    }
                });
                // Llamar al servicio para enviar el producto
                let response;
                if (isEditing.value) {
                    response = await ProductoService.updateProducto(productoData.id, formData);
                }
                else {
                    response = await ProductoService.addProducto(formData);
                }
                console.log(response);
                if (response && response.exito) {
                    succesToast.fire({
                        text: isEditing.value ? "Producto actualizado correctamente." : "Producto agregado correctamente.",
                        icon: "success",
                    });
                    // Redirigir a /panel/productos
                    router.push("/panel/productos");
                }
            }
            catch (error) {
                console.error("Error al guardar el producto:", error);
                errorToast.fire({
                    text: "Hubo un problema al guardar el producto.",
                    icon: "error",
                });
            }
            finally {
                isLoading.value = false;
            }
        }
        function handleFileChange(event) {
            const target = event.target;
            if (target.files) {
                const files = Array.from(target.files);
                // Verificar cuántas imágenes hay actualmente
                const currentImageCount = productoForm.value.imagenesDetalle.length;
                // Si ya hay 5 o más imágenes, mostrar advertencia
                if (currentImageCount >= 5) {
                    Swal.fire({
                        title: "Límite alcanzado",
                        text: "Solo se pueden agregar un máximo de 5 imágenes.",
                        icon: "warning",
                        toast: true,
                        position: "top-end",
                        showConfirmButton: false,
                        timer: 3000,
                        customClass: {
                            popup: "swal-toast-custom",
                        },
                    });
                    return;
                }
                // Limitar la cantidad de imágenes a agregar
                const maxImages = 5 - currentImageCount;
                const imagesToAdd = files.slice(0, maxImages);
                imagesToAdd.forEach((file) => {
                    // Crear un objeto URL del archivo para la vista previa
                    const imageUrl = URL.createObjectURL(file);
                    // Agregar el archivo real en el campo `imagen` y la URL para la vista previa en `imagenUrl`
                    productoForm.value.imagenesDetalle.push({
                        id: productoForm.value.imagenesDetalle.length + 1,
                        productoId: 0,
                        imagen: file, // Guardamos el File real para enviar al servidor
                        portada: false,
                        imagenUrl: imageUrl, // Guardamos la URL generada para la vista previa
                    });
                });
            }
        }
        function removeImage(index) {
            const removedImage = productoForm.value.imagenesDetalle[index];
            productoForm.value.imagenesDetalle.splice(index, 1);
            if (removedImage.portada &&
                productoForm.value.imagenesDetalle.length > 0) {
                // Si la imagen eliminada era la portada, asignar una nueva portada (opcional)
                productoForm.value.imagenesDetalle[0].portada = true;
            }
        }
        function setPortada(index) {
            productoForm.value.imagenesDetalle.forEach((img, idx) => {
                img.portada = idx === index; // Solo una imagen es portada
            });
        }
        async function getCameraDevices() {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const cameras = devices.filter(device => device.kind === "videoinput");
                videoDevices.value = cameras;
                if (cameras.length > 0 && !selectedDeviceId.value) {
                    // Seleccionar la primera cámara disponible por defecto
                    selectedDeviceId.value = cameras[0].deviceId;
                }
            }
            catch (err) {
                console.error("Error al listar los dispositivos de cámara:", err);
            }
        }
        async function openCamera() {
            isCameraOpen.value = true;
            // Esperar a que Vue actualice el DOM para que el reproductor de video esté montado
            await nextTick();
            // Detener cualquier stream anterior activo
            if (cameraStream) {
                cameraStream.getTracks().forEach(t => t.stop());
                cameraStream = null;
            }
            // Configurar restricciones dinámicamente si hay un dispositivo seleccionado
            const videoConstraints = selectedDeviceId.value
                ? {
                    deviceId: { exact: selectedDeviceId.value },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
                : {
                    facingMode: { ideal: cameraFacing.value },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                };
            const constraints = {
                video: videoConstraints
            };
            try {
                try {
                    cameraStream = await navigator.mediaDevices.getUserMedia(constraints);
                }
                catch (err) {
                    console.warn("Fallo con facingMode ideal o resolución ideal, probando constraint básico:", err);
                    // Fallback a cualquier cámara de video disponible
                    cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
                }
                const videoEl = video.value || document.querySelector("video");
                if (videoEl) {
                    videoEl.srcObject = cameraStream;
                    videoEl.setAttribute("playsinline", "true");
                    videoEl.setAttribute("autoplay", "true");
                    videoEl.muted = true; // Silenciar para evitar bucles de audio accidentales
                    // Forzar la reproducción del stream en iOS/Safari
                    try {
                        await videoEl.play();
                    }
                    catch (playError) {
                        console.error("Error al reproducir el flujo de la cámara:", playError);
                    }
                }
                else {
                    console.error("El elemento de video no fue encontrado en el DOM después del nextTick.");
                }
                // Obtener la lista de cámaras (después del primer acceso exitoso para asegurar permisos de nombres/labels)
                await getCameraDevices();
            }
            catch (error) {
                console.error("Error accessing the camera:", error);
                Swal.fire({
                    title: 'Cámara no disponible',
                    text: 'No se pudo acceder a la cámara. Por favor verifica que hayas concedido los permisos en tu navegador y que estés navegando bajo una conexión segura (HTTPS).',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                isCameraOpen.value = false;
            }
        }
        function onCameraDeviceChange() {
            // Reiniciar cámara con el nuevo ID de dispositivo seleccionado
            openCamera();
        }
        function switchCamera() {
            cameraFacing.value = cameraFacing.value === 'environment' ? 'user' : 'environment';
            // Limpiar selectedDeviceId para permitir cambiar a facingMode
            selectedDeviceId.value = "";
            // Detener el hardware antes de volver a solicitarlo
            if (cameraStream) {
                cameraStream.getTracks().forEach(t => t.stop());
                cameraStream = null;
            }
            const videoEl = video.value || document.querySelector("video");
            if (videoEl) {
                videoEl.srcObject = null;
            }
            openCamera();
        }
        function closeCamera() {
            // 1. Detener primero los tracks del hardware mientras el stream y el video sigan montados
            if (cameraStream) {
                cameraStream.getTracks().forEach((track) => track.stop());
                cameraStream = null;
            }
            // 2. Desvincular el stream del video
            const videoEl = video.value || document.querySelector("video");
            if (videoEl) {
                videoEl.srcObject = null;
            }
            // 3. Cerrar el modal/slide-in y remover el video del DOM
            isCameraOpen.value = false;
        }
        const captureImage = async () => {
            const videoEl = video.value || document.querySelector("video");
            if (!videoEl || !videoEl.videoWidth) {
                console.error("El video no está listo para capturar fotos.");
                Swal.fire('Cámara no lista', 'La cámara no está lista para capturar fotos todavía. Por favor espera a que se inicie el flujo de video.', 'warning');
                return;
            }
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (!context)
                return;
            canvas.width = videoEl.videoWidth;
            canvas.height = videoEl.videoHeight;
            context.drawImage(videoEl, 0, 0);
            const dataURL = canvas.toDataURL("image/png");
            const imageFile = dataURLToFile(dataURL, "captura.png");
            if (removeGreenScreen.value) {
                const bgRemovedImage = await removeBackground(imageFile);
                if (bgRemovedImage) {
                    agregarImagen(bgRemovedImage);
                }
                else {
                    Swal.fire({
                        title: "Error",
                        text: "Hubo un problema al eliminar el fondo. Se usará la imagen original.",
                        icon: "error",
                        confirmButtonText: "Aceptar",
                    });
                    agregarImagen(imageFile);
                }
            }
            else {
                agregarImagen(imageFile);
            }
        };
        const removeBackground = async (imageFile) => {
            const formData = new FormData();
            formData.append("image_file", imageFile);
            formData.append("size", "auto");
            try {
                const response = await fetch("https://api.remove.bg/v1.0/removebg", {
                    method: "POST",
                    headers: { "X-Api-Key": apiKey },
                    body: formData,
                });
                if (!response.ok)
                    throw new Error(`Error en remove.bg: ${response.status}`);
                const blob = await response.blob();
                return new File([blob], "captura_sin_fondo.png", { type: "image/png" });
            }
            catch (error) {
                console.error("Error al eliminar el fondo:", error);
                return null;
            }
        };
        const agregarImagen = (file) => {
            if (productoForm.value.imagenesDetalle.length >= 5) {
                Swal.fire({
                    title: "Límite alcanzado",
                    text: "Solo se pueden agregar un máximo de 5 imágenes.",
                    icon: "warning",
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    customClass: { popup: "swal-toast-custom" },
                });
                return;
            }
            const imageUrl = URL.createObjectURL(file);
            productoForm.value.imagenesDetalle.push({
                id: Date.now(),
                productoId: 0,
                imagen: file, // Agregar la imagen procesada o capturada
                portada: productoForm.value.imagenesDetalle.length === 0, // La primera imagen será portada
                imagenUrl: imageUrl, // Vista previa opcional
            });
        };
        function dataURLToFile(dataURL, fileName) {
            const arr = dataURL.split(",");
            // Verifica si hay una coincidencia para el tipo MIME
            const mimeMatch = arr[0].match(/:(.*?);/);
            if (!mimeMatch) {
                throw new Error("Tipo MIME no encontrado en el dataURL");
            }
            const mime = mimeMatch[1]; // Extrae el tipo MIME si la coincidencia es válida
            const bstr = atob(arr[1]); // Decodifica el Base64 a binario
            const n = bstr.length;
            const u8arr = new Uint8Array(n);
            for (let i = 0; i < n; i++) {
                u8arr[i] = bstr.charCodeAt(i); // Convierte el binario en un array de bytes
            }
            return new File([u8arr], fileName, {
                type: mime,
                lastModified: Date.now(),
            });
        }
        function selectRepuesto(repuesto) {
            searchRepuestos.value = repuesto.nombre;
            selectedRepuesto.value = repuesto.repuestoID;
            handleRepuestoChange();
            repuestos.value = [];
        }
        function searchRepuesto() {
            if (searchRepuestos.value.length >= 3) {
                fetchRepuestos();
            }
            else {
                repuestos.value = [];
            }
        }
        const filteredRepuestos = computed(() => {
            if (searchRepuestos.value.length < 3) {
                return []; // No mostrar nada si la búsqueda es muy corta
            }
            const searchTerm = searchRepuestos.value.toLowerCase();
            // 'repuestos.value' ya está ordenado alfabéticamente desde fetchRepuestos
            // Solo necesitamos filtrar y mapear para el resaltado.
            return repuestos.value
                .filter((repuesto) => String(repuesto.nombre || '').toLowerCase().includes(searchTerm))
                .map((repuesto) => {
                // Resaltar el término buscado con <mark>
                const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi");
                const highlightedNombre = String(repuesto.nombre || '').replace(regex, `<mark>$1</mark>`);
                return {
                    ...repuesto,
                    highlightedNombre, // Agregar la versión resaltada
                };
            });
        });
        // Método para obtener repuestos de la API
        const fetchRepuestos = async () => {
            try {
                let items = await RepuestoService.getAllRepuestos();
                if (Array.isArray(items)) {
                    // Ordenar los repuestos alfabéticamente por 'nombre'
                    items.sort((a, b) => {
                        const nameA = String(a.nombre || '').toLowerCase();
                        const nameB = String(b.nombre || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                }
                repuestos.value = items || []; // Asignar la lista ordenada o un array vacío
            }
            catch (error) {
                console.error("Error al cargar y ordenar repuestos:", error);
                repuestos.value = []; // Asegurar que sea un array vacío en caso de error
            }
        };
        const fetchAtributosDesplegables = async (repuestoId) => {
            try {
                const atributosDesdeAPI = await RepuestoService.getAtributosDesplegablesByRepuestoId(repuestoId);
                if (Array.isArray(atributosDesdeAPI)) {
                    productoForm.value.atributosDesplegables = atributosDesdeAPI.map((atributoOriginal) => {
                        let valoresOriginales = [];
                        let esAtributoRef = false;
                        // Verificar la estructura del atributo y sus valores
                        if (typeof atributoOriginal === "object" && atributoOriginal !== null && atributoOriginal.nombreAtributo) {
                            esAtributoRef = atributoOriginal.nombreAtributo.nombreAtributo === 'Ref';
                            if (Array.isArray(atributoOriginal.nombreAtributo.valores)) {
                                valoresOriginales = atributoOriginal.nombreAtributo.valores;
                            }
                        }
                        else {
                            console.warn("Estructura de atributoOriginal.nombreAtributo no válida o faltante:", atributoOriginal);
                        }
                        // Ordenar los valores si no es el atributo 'Ref' y tiene valores
                        const valoresOrdenados = !esAtributoRef && valoresOriginales.length > 0
                            ? [...valoresOriginales].sort((a, b) => {
                                const valorA = String(a.valor || '').toLowerCase();
                                const valorB = String(b.valor || '').toLowerCase();
                                return valorA.localeCompare(valorB);
                            })
                            : []; // Si es 'Ref' o no hay valores, dejar vacío o según necesites
                        return {
                            ...atributoOriginal,
                            nombreAtributo: {
                                ...atributoOriginal.nombreAtributo,
                                valoresOrdenados: valoresOrdenados,
                            },
                            selectedValue: (!esAtributoRef && (atributoOriginal.nombreAtributo?.tipoAtributo === undefined || atributoOriginal.nombreAtributo?.tipoAtributo === 'desplegable'))
                                ? (atributoOriginal.selectedValue || "") : null,
                            ref: esAtributoRef ? (atributoOriginal.ref || '') : null,
                            valorLibre: esAtributoRef ? (atributoOriginal.ref || '') : (atributoOriginal.nombreAtributo?.tipoAtributo === 'booleano' ? 'false' : ""),
                            valorMin: null,
                            valorMax: null,
                            valorLibreFormatted: "",
                            valorMinFormatted: "",
                            valorMaxFormatted: ""
                        };
                    });
                }
                else {
                    console.error("La API devolvió un formato inesperado para atributos:", atributosDesdeAPI);
                    productoForm.value.atributosDesplegables = [];
                }
                // console.log("Atributos desplegables procesados:", productoForm.value.atributosDesplegables);
            }
            catch (error) {
                console.error("Error al cargar y procesar atributos desplegables:", error);
                productoForm.value.atributosDesplegables = [];
            }
        };
        const handleRepuestoChange = () => {
            if (selectedRepuesto.value) {
                fetchAtributosDesplegables(selectedRepuesto.value);
            }
            else {
                productoForm.value.atributosDesplegables = [];
            }
        };
        function selectModelo(modelo) {
            searchModelos.value = modelo.nombre;
            selectedModelo.value = modelo.modeloID;
            modelos.value = [];
        }
        function searchModelo() {
            if (searchModelos.value.length >= 3) {
                fetchModelos();
            }
            else {
                modelos.value = [];
            }
        }
        const filteredModelos = computed(() => {
            if (searchModelos.value.length < 3) {
                return [];
            }
            return modelos.value.filter((modelo) => modelo.nombre.toLowerCase().includes(searchModelos.value.toLowerCase()));
        });
        // Método para obtener modelo de la API
        const fetchModelos = async () => {
            try {
                modelos.value = await ModeloService.getAllModelos();
            }
            catch (error) {
                console.error("Error al cargar modelos:", error);
                // Agregar manejo de errores para la interfaz de usuario
            }
        };
        function selectMarca(marca) {
            searchMarcas.value = marca.nombre;
            selectedMarca.value = marca.marcaID;
            marcas.value = [];
        }
        function searchMarca() {
            if (searchMarcas.value.length >= 3) {
                fetchMarcas();
            }
            else {
                marcas.value = [];
            }
        }
        const filteredMarcas = computed(() => {
            if (searchMarcas.value.length < 3) {
                return [];
            }
            return marcas.value.filter((marca) => marca.nombre.toLowerCase().includes(searchMarcas.value.toLowerCase()));
        });
        // Método para obtener marcas de la API
        const fetchMarcas = async () => {
            try {
                marcas.value = await MarcaService.getAllMarcas();
            }
            catch (error) {
                console.error("Error al cargar modelos:", error);
                // Agregar manejo de errores para la interfaz de usuario
            }
        };
        // Toggle function to add or remove color from the selection
        const toggleColor = (color) => {
            const currentColors = coloresSeleccionados.value;
            const index = currentColors.indexOf(color);
            if (index === -1) {
                // Add color if not already selected
                coloresSeleccionados.value = [...currentColors, color];
            }
            else {
                // Remove color if already selected
                coloresSeleccionados.value = currentColors.filter((c) => c !== color);
            }
        };
        const updateSelectedValue = (index, value) => {
            console.log("Índice recibido:", index);
            console.log("Atributos actuales antes de actualizar:", productoForm.value.atributosDesplegables);
            if (!productoForm.value.atributosDesplegables[index]) {
                console.error(`El índice ${index} no es válido.`);
                return;
            }
            productoForm.value.atributosDesplegables[index].valorID = value === null ? 0 : Number(value);
            console.log("Atributos después de actualizar:", productoForm.value.atributosDesplegables);
        };
        onMounted(async () => {
            await fetchRepuestos();
            await fetchModelos();
            await fetchMarcas();
            const id = route.params.id;
            if (id) {
                isEditing.value = true;
                await loadProducto(Number(id));
            }
        });
        async function loadProducto(id) {
            isLoading.value = true;
            try {
                const response = await ProductoService.getProductoById(id);
                if (response.exito && response.datos) {
                    const prod = response.datos;
                    // Mapear los datos al formulario
                    productoForm.value = {
                        id: prod.id,
                        nombre: prod.nombre,
                        codigoBarrasSistema: prod.codigoBarrasSistema,
                        codigoBarrasReferencia: prod.codigoBarrasReferencia,
                        modeloID: prod.modeloID,
                        repuestoID: prod.repuestoID,
                        codigoUnico: prod.codigoUnico,
                        marcaId: prod.marcaId,
                        color: prod.color,
                        detalles: prod.detalles,
                        precioVenta: prod.precioVenta,
                        pesoKG: prod.pesoKG,
                        activo: prod.activo,
                        imagenesDetalle: (prod.imagenes || []).map((img) => {
                            const url = img.imagen || "";
                            const fullUrl = url.startsWith('http') ? url : (url.startsWith('/') ? `${config.API_URL}${url}` : `${config.API_URL}/${url}`);
                            return {
                                id: img.id,
                                productoId: img.productoId || 0,
                                imagenUrl: fullUrl,
                                portada: img.portada,
                                imagen: null
                            };
                        }),
                        atributosDesplegables: []
                    };
                    console.log("Formulario mapeado:", productoForm.value);
                    // Actualizar buscadores y disparar cargas dependientes
                    if (prod.repuestoID) {
                        selectedRepuesto.value = prod.repuestoID;
                        const rep = repuestos.value.find(r => r.repuestoID === prod.repuestoID);
                        if (rep)
                            searchRepuestos.value = rep.nombre;
                        // Cargar atributos manualmente ya que el watch podría no dispararse a tiempo o necesitar el valor
                        await fetchAtributosDesplegables(prod.repuestoID);
                        // Mapear los valores de los atributos si vienen en la respuesta
                        if (prod.desplegables && prod.desplegables.length > 0) {
                            console.log("Mapeando atributos guardados:", prod.desplegables);
                            productoForm.value.atributosDesplegables.forEach(attr => {
                                const attrId = attr.nombreAtributoID || (attr.nombreAtributo && attr.nombreAtributo.nombreAtributoID);
                                const valorGuardado = prod.desplegables.find((d) => d.nombreAtributoID === attrId);
                                if (valorGuardado) {
                                    console.log(`Valor encontrado para atributo ${attrId}:`, valorGuardado);
                                    attr.selectedValue = valorGuardado.valorID;
                                    attr.valorLibre = valorGuardado.valorLibre;
                                    attr.valorMin = valorGuardado.valorMin;
                                    attr.valorMax = valorGuardado.valorMax;
                                    // Si es numérico o rango, también formatear para los inputs
                                    if (attr.nombreAtributo?.tipoAtributo === 'numerico') {
                                        attr.valorLibreFormatted = valorGuardado.valorLibre || "";
                                    }
                                    else if (attr.nombreAtributo?.tipoAtributo === 'rango') {
                                        attr.valorMinFormatted = valorGuardado.valorMin || "";
                                        attr.valorMaxFormatted = valorGuardado.valorMax || "";
                                    }
                                }
                            });
                        }
                    }
                    if (prod.modeloID) {
                        selectedModelo.value = prod.modeloID;
                        const mod = modelos.value.find(m => m.modeloID === prod.modeloID);
                        if (mod)
                            searchModelos.value = mod.nombre;
                    }
                    if (prod.marcaId) {
                        selectedMarca.value = prod.marcaId;
                        const mar = marcas.value.find(m => m.marcaID === prod.marcaId);
                        if (mar)
                            searchMarcas.value = mar.nombre;
                    }
                }
                else {
                    console.warn("No se encontró el producto o la respuesta no fue exitosa:", response);
                }
            }
            catch (error) {
                console.error("Error al cargar producto para editar:", error);
            }
            finally {
                isLoading.value = false;
            }
        }
        watch(() => route.params.id, (newId) => {
            if (newId) {
                isEditing.value = true;
                loadProducto(Number(newId));
            }
            else {
                isEditing.value = false;
                // Reset form if needed
            }
        });
        return {
            video,
            videoDevices,
            selectedDeviceId,
            onCameraDeviceChange,
            updateSelectedValue,
            saveProducto,
            productoForm,
            handleFileChange,
            removeImage,
            setPortada,
            openCamera,
            closeCamera,
            switchCamera,
            captureImage,
            isCameraOpen,
            repuestos,
            selectedRepuesto,
            searchRepuestos,
            searchRepuesto,
            filteredRepuestos,
            selectRepuesto,
            modelos,
            selectedModelo,
            searchModelos,
            searchModelo,
            filteredModelos,
            selectModelo,
            selectedMarca,
            searchMarcas,
            searchMarca,
            filteredMarcas,
            selectMarca,
            coloresSeleccionados,
            coloresDisponibles,
            toggleColor,
            applyColor,
            atributosDesplegables,
            handleRepuestoChange,
            removeGreenScreen,
            onPrecioVentaInput,
            precioVentaFormatted,
            onAttributeInput,
            getPlaceholder,
            isLoading,
            isEditing
        };
    },
};
